import { createAtom, makeAutoObservable, runInAction } from "mobx";
import { type AnalyticsSource, track } from "@/lib/analytics";
import { listChanges } from "@/lib/changed-fields";
import { singleTaskProps, trackTaskWrite } from "@/lib/task-analytics";
import type { Query, QueryClient } from "@tanstack/react-query";
import { getQueryClient, peekQueryClient } from "@/queries/client";
import {
  findCachedWorkItem,
  invalidateWorkItemLists,
  patchCachedWorkItem,
  patchCachedWorkItems,
  removeCachedSubTask,
  removeCachedWorkItems,
  appendCachedSubTask,
  subTaskListsOf,
  workItemDetailQuery,
  writeWorkItemRows,
} from "@/queries/work-items";
import { scopeIfReady } from "@/queries/scope";
import { markWorkItemActivityStale } from "@/queries/work-item-feed";
import { rereadWorkItemRelations } from "@/queries/work-item-links";
import { queryCacheScope } from "./query-cache-scope";
import type { RootStore } from "./root.store";
import type { TIssue, TWorkItemAssigneeDetail } from "@/types/issue";

/** The query cache the work-item lists live in. */
interface WorkItemCache {
  readonly client: QueryClient;
  readonly tenant: string;
}

/**
 * The work-item writers (NG-2806, decision 3). The lists and full records are
 * read through the query cache (`queries/work-items.ts`), and every writer here
 * patches the row by id across every cached list and record; nothing keeps a
 * copy of a row. `getIssueById` answers from the cache for MobX readers.
 */
export class IssueStore {
  private predecessorReads = new Map<string, number>();

  private rootStore: RootStore;

  // Tells MobX readers of `getIssueById` that a cached work-item list or full
  // record changed,
  // so an observer re-renders on a cache patch or re-read without any copy of
  // the rows living here (NG-2806, decision 3).
  private cacheAtom = createAtom("work-item lists");
  private cacheSubscription: { client: QueryClient; unsubscribe: () => void } | null = null;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    // exception: autoBind intentionally omitted — tests monkey-patch actions
    // such as `createIssue` via vi.spyOn (issue-create-dialog.test.tsx). action.bound installs bound
    // actions as non-configurable. Tracked as a follow-up; refactor tests to
    // mock at the issue service boundary.
    makeAutoObservable(this, {
      rootStore: false,
      cacheAtom: false,
      cacheSubscription: false,
      predecessorReads: false,
    } as never);
  }

  /**
   * The server logs a property or date change in the item's history: mark the
   * cached history stale so an open Activity panel re-reads it. The history is
   * read only through the cache (NG-2806).
   */
  private markActivityStale(workspaceSlug: string, issueId: string): void {
    const client = peekQueryClient();
    const scope = client ? queryCacheScope(workspaceSlug, "work-item-history") : null;
    if (client && scope) markWorkItemActivityStale(client, scope, issueId);
  }

  /** The cache a write to `workspaceSlug`'s work items patches. */
  private workItemCache(workspaceSlug: string): WorkItemCache {
    return { client: getQueryClient(), tenant: workspaceSlug };
  }

  /**
   * The cache of the workspace on screen, for a reader or writer that is not
   * told one; `null` with no workspace, or before the app has a client.
   */
  private currentCache(): WorkItemCache | null {
    const tenant = this.rootStore.router.workspaceSlug;
    const client = peekQueryClient();
    return tenant && client ? { client, tenant } : null;
  }

  /** Observe the cache's work-item lists and records for `getIssueById`'s MobX readers. */
  private observeCache(cache: WorkItemCache): void {
    if (this.cacheSubscription?.client !== cache.client) {
      this.cacheSubscription?.unsubscribe();
      const unsubscribe = cache.client.getQueryCache().subscribe((event) => {
        const domain = event.query.queryKey[2];
        if (event.type !== "updated" || (domain !== "work-items" && domain !== "work-item")) return;
        runInAction(() => this.cacheAtom.reportChanged());
      });
      this.cacheSubscription = { client: cache.client, unsubscribe };
    }
    this.cacheAtom.reportObserved();
  }

  /**
   * The row a writer starts from: the full record the task page or the side
   * panel read (`workItemDetailQuery`), else a cached list row.
   */
  private rowBefore(cache: WorkItemCache, issueId: string): TIssue | undefined {
    return findCachedWorkItem(cache.client, cache.tenant, issueId);
  }

  /**
   * Merge `fields` into the work item's row wherever it is held: the lists' rows
   * and the full records in the cache.
   */
  private patchRow(cache: WorkItemCache, issueId: string, fields: Partial<TIssue>): void {
    patchCachedWorkItem(cache.client, cache.tenant, issueId, fields);
  }

  /**
   * Run a write to `issueIds` under the cache's write guard: cancel list reads,
   * put back only these rows on failure, re-read the lists read meanwhile.
   */
  private guardWrite<T>(
    cache: WorkItemCache,
    issueIds: readonly string[],
    write: () => Promise<T>,
    options: { optimistic?: () => void; relist?: boolean | ((query: Query) => boolean) } = {},
  ): Promise<T> {
    return writeWorkItemRows(cache.client, cache.tenant, issueIds, write, options);
  }

  /**
   * A work item of the workspace on screen as the cache holds it: its full
   * record if read, else a list's row of it (a series' own row before its
   * occurrences). Re-runs its MobX readers when the cache changes.
   */
  getIssueById(issueId: string): TIssue | undefined {
    const cache = this.currentCache();
    if (!cache) return undefined;
    this.observeCache(cache);
    return this.rowBefore(cache, issueId);
  }

  /** Merge a newer copy of a work item into every cached row and record of it. */
  upsertIssue(issue: TIssue): void {
    const cache = this.currentCache();
    if (cache) patchCachedWorkItem(cache.client, cache.tenant, issue.id, issue);
  }

  /**
   * Clear a job role from every cached work item that holds it. The backend
   * unassigns the role across all work items when a role is deleted or
   * deactivated (a SET_NULL cascade run explicitly), but the board/list/detail
   * views render from this store's cache — so without this sweep the deleted
   * role's chip lingers on already-loaded items until a refetch. Mutating only
   * the affected items keeps the rest of the cache (and their pagination) intact.
   */
  clearJobRoleFromCache(jobRoleId: string): void {
    const cache = this.currentCache();
    if (!cache) return;
    patchCachedWorkItems(
      cache.client,
      cache.tenant,
      (row) => row.job_role_id === jobRoleId,
      (row) => ({ ...row, job_role_id: null, job_role_detail: null }),
    );
  }

  /**
   * Reflect a Smart Assign in the cache: the given work items had their job role
   * bulk-assigned to a person, so the backend cleared the role and set the
   * assignee (person beats role). The board/list/detail render from this cache,
   * so without this the assignee column keeps showing the now-stale job role.
   * Mutate only the affected items — clear the role and set the single assignee.
   *
   * Carry the assignee's `detail` onto `assignee_details` too, not just the id:
   * the assignee cell resolves a name from the loaded project-members map OR the
   * item's own `assignee_details`, and the smart-assigned person may not be in
   * the project-members map the current view loaded (they're a workspace member,
   * not necessarily a project member). Without the detail the cell can't resolve
   * the id and renders empty until a refetch (which is when the server sends the
   * detail). Seeding it here makes the assignee show immediately.
   */
  applySmartAssignToCache(
    workItemIds: string[],
    assigneeId: string,
    detail?: TWorkItemAssigneeDetail,
  ): void {
    const ids = new Set(workItemIds);
    const assigned = (row: TIssue): TIssue => ({
      ...row,
      job_role_id: null,
      job_role_detail: null,
      assignee_ids: [assigneeId],
      assignee_details: detail ? [detail] : [],
    });
    const cache = this.currentCache();
    if (!cache) return;
    // Every occurrence row of an assigned series carries the assignee too.
    patchCachedWorkItems(cache.client, cache.tenant, (row) => ids.has(row.id), assigned);
  }

  async createIssue(
    workspaceSlug: string,
    projectId: string,
    data: Partial<TIssue>,
    source: AnalyticsSource = "page",
  ): Promise<TIssue> {
    const issue = await this.rootStore.workItemService.createWorkItem(
      workspaceSlug,
      projectId,
      data,
    );
    // Which lists the new item joins (and where) is the server's answer.
    this.relist(this.workItemCache(workspaceSlug));
    track("task_created", {
      task_id: issue.id,
      project_id: issue.project_id ?? projectId,
      category: issue.category ?? null,
      is_sub_task: Boolean(issue.parent_id),
      source,
    });
    return issue;
  }

  /**
   * Create a sub-task under `parentId` (NG-2806). It joins the parent's cached
   * children at once and the parent's count follows; the lists' re-read, which
   * the create starts, confirms both from the server.
   */
  async createSubIssue(
    workspaceSlug: string,
    projectId: string,
    parentId: string,
    data: Partial<TIssue> & { name: string },
    source: AnalyticsSource = "page",
  ): Promise<TIssue> {
    const child = await this.createIssue(
      workspaceSlug,
      projectId,
      { ...data, parent_id: parentId },
      source,
    );
    const cache = this.workItemCache(workspaceSlug);
    const parent = this.rowBefore(cache, parentId);
    runInAction(() => {
      this.patchRow(cache, parentId, { sub_issues_count: (parent?.sub_issues_count ?? 0) + 1 });
    });
    appendCachedSubTask(cache.client, cache.tenant, parentId, child);
    return child;
  }

  /**
   * Create a standalone (project-less) event — #3754.
   *
   * Which lists the new event joins is the server's answer, so the lists are
   * re-read, as after any create.
   */
  async createStandaloneEvent(
    workspaceSlug: string,
    data: Partial<TIssue>,
    source: AnalyticsSource = "page",
  ): Promise<TIssue> {
    const issue = await this.rootStore.workItemService.createWorkspaceWorkItem(workspaceSlug, data);
    this.relist(this.workItemCache(workspaceSlug));
    track("task_created", {
      task_id: issue.id,
      project_id: null,
      category: issue.category ?? null,
      is_sub_task: false,
      source,
    });
    return issue;
  }

  async updateIssue(
    workspaceSlug: string,
    projectId: string,
    issueId: string,
    data: Partial<TIssue>,
    source: AnalyticsSource = "page",
  ): Promise<TIssue> {
    // A project-less work item (a workspace-level event) has no project segment,
    // so the project-scoped route builds `/projects//work-items/<id>/` and 404s
    // — the NG-1082 bug where an event's assignment never sticks. Route it to the
    // workspace-scoped update instead; every updateIssue caller (assignee cell,
    // inline cells, gantt, calendar, board, table columns) gets the fix at once,
    // and this also skips the project-scoped history route the activity panel
    // reads, which would 404 the same way.
    if (!projectId) {
      return this.updateWorkspaceIssue(workspaceSlug, issueId, data, source);
    }
    const cache = this.workItemCache(workspaceSlug);
    const existing = this.rowBefore(cache, issueId);
    const backup = existing ? { ...existing } : undefined;
    // A move to another parent (NG-3470) changes which children both parents
    // list and how many they count, which no patch of this row can say: both
    // parents' children are re-read where on screen, and their records go stale.
    const movedBetween = new Set(
      "parent_id" in data
        ? [backup?.parent_id, data.parent_id].filter((id): id is string => Boolean(id))
        : [],
    );
    // A promote (a detach, NG-3429) also adds a row to every top-level list, so
    // every list is re-read, the former parent's children with them. It is not
    // shown before the server answers: the page's Parent and Type rows changing
    // is then the sign it persisted, and a refusal (no edit on the parent) never
    // flashes the item out of its parent. A move between parents stays
    // optimistic, since its Parent field holds the pick and locks until the save
    // settles (NG-3470), which a promote, with no field to hold, cannot do.
    const detached = "parent_id" in data && data.parent_id === null;
    const issue = await this.guardWrite(
      cache,
      [issueId, ...movedBetween],
      () => this.rootStore.workItemService.updateWorkItem(workspaceSlug, projectId, issueId, data),
      {
        optimistic: () => {
          if (!backup || detached) return;
          runInAction(() => {
            this.patchRow(cache, issueId, data);
          });
        },
        relist: detached || (movedBetween.size > 0 && subTaskListsOf(cache.tenant, movedBetween)),
      },
    );
    runInAction(() => {
      this.putServerRow(cache, issue);
    });
    trackTaskWrite(backup, issue, data, source);
    // Property changes land in the activity log too; revalidate an
    // already-open feed so it doesn't go stale next to the editor.
    this.markActivityStale(workspaceSlug, issueId);
    return issue;
  }

  /** Re-read every work-item list: a write changed which rows they hold. */
  private relist(cache: WorkItemCache): void {
    invalidateWorkItemLists(cache.client, cache.tenant).catch((error: unknown) => {
      console.warn("[work-items] re-reading lists after a write failed", {
        tenant: cache.tenant,
        error,
      });
    });
  }

  /** Drop rows the server has deleted at once (a slow re-read must not leave them on screen), then re-read. */
  private dropRows(cache: WorkItemCache, issueIds: readonly string[]): void {
    removeCachedWorkItems(cache.client, cache.tenant, issueIds);
    this.relist(cache);
  }

  /** The server's answer for a written row, merged over the row it replaces. */
  private putServerRow(cache: WorkItemCache, issue: TIssue): void {
    this.patchRow(cache, issue.id, issue);
  }

  async updateWorkspaceIssue(
    workspaceSlug: string,
    issueId: string,
    data: Partial<TIssue>,
    source: AnalyticsSource = "page",
  ): Promise<TIssue> {
    const cache = this.workItemCache(workspaceSlug);
    const existing = this.rowBefore(cache, issueId);
    const backup = existing ? { ...existing } : undefined;
    const issue = await this.guardWrite(
      cache,
      [issueId],
      () => this.rootStore.workItemService.updateWorkspaceWorkItem(workspaceSlug, issueId, data),
      {
        optimistic: () => {
          if (!backup) return;
          runInAction(() => {
            this.patchRow(cache, issueId, data);
          });
        },
      },
    );
    runInAction(() => {
      this.putServerRow(cache, issue);
    });
    trackTaskWrite(backup, issue, data, source);
    return issue;
  }

  async setPredecessors(
    workspaceSlug: string,
    projectId: string,
    issueId: string,
    sequenceIds: (number | string)[],
    source: AnalyticsSource = "table_inline",
  ): Promise<void> {
    const cache = this.workItemCache(workspaceSlug);
    const before = new Set((this.rowBefore(cache, issueId)?.predecessors ?? []).map((p) => p.id));
    // The endpoint reconciles the relations server-side and returns the
    // authoritative predecessor list, so we apply that rather than guessing
    // optimistically (the row may live under any collection scope).
    const { predecessors } = await this.guardWrite(cache, [issueId], () =>
      this.rootStore.workItemService.setPredecessors(
        workspaceSlug,
        projectId,
        issueId,
        sequenceIds,
      ),
    );
    this.applyPredecessors(cache, issueId, predecessors);
    // The reconcile can add and remove at once, so report each side with its
    // count against the list this row showed before (NG-2056).
    const { added, removed } = listChanges(
      before,
      (predecessors ?? []).map((p) => p.id),
    );
    const props = { task_id: issueId, project_id: projectId, source };
    if (added > 0) track("dependency_created", { ...props, count: added });
    if (removed > 0) track("dependency_deleted", { ...props, count: removed });
  }

  /**
   * Re-read a loaded work item's `predecessors` after a dependency write that
   * did not return them (NG-2891). The blocked marker on the table row and the
   * side panel's "Blocked by N items" note both read `predecessors` from the
   * item's row, so without this they keep the count from when it loaded.
   *
   * Patches `predecessors` alone, wherever the row lives (`patchRow`): the read
   * returns the full record, but the row may be mid-edit elsewhere, and only
   * this field changed. A no-op for an item nothing has loaded, since nothing
   * renders it. The newest read wins when two writes in a row race their
   * re-reads.
   */
  async refreshPredecessors(workspaceSlug: string, issueId: string): Promise<void> {
    const cache = this.workItemCache(workspaceSlug);
    if (!this.rowBefore(cache, issueId)) return;
    const seq = (this.predecessorReads.get(issueId) ?? 0) + 1;
    this.predecessorReads.set(issueId, seq);
    const { predecessors } = await this.rootStore.workItemService.getWorkItemByIdentifier(
      workspaceSlug,
      issueId,
    );
    if (this.predecessorReads.get(issueId) !== seq) return;
    this.applyPredecessors(cache, issueId, predecessors);
  }

  private applyPredecessors(
    cache: WorkItemCache,
    issueId: string,
    predecessors: TIssue["predecessors"],
  ): void {
    runInAction(() => {
      this.patchRow(cache, issueId, { predecessors });
    });
  }

  async addIssueSkill(
    workspaceSlug: string,
    // Empty / null for a project-less work item; the service routes on it.
    projectId: string | null | undefined,
    issueId: string,
    skillId: string,
    source: AnalyticsSource = "page",
  ): Promise<void> {
    const cache = this.workItemCache(workspaceSlug);
    const existing = this.rowBefore(cache, issueId);
    if (!existing || existing.skill_ids.includes(skillId)) return;
    await this.guardWrite(
      cache,
      [issueId],
      () =>
        this.rootStore.workspaceSkillService.addIssueSkills(workspaceSlug, projectId, issueId, [
          skillId,
        ]),
      {
        optimistic: () =>
          runInAction(() =>
            this.patchRow(cache, issueId, { skill_ids: [...existing.skill_ids, skillId] }),
          ),
      },
    );
    track("task_skill_added", {
      task_id: issueId,
      project_id: projectId || null,
      skill_id: skillId,
      source,
    });
  }

  async removeIssueSkill(
    workspaceSlug: string,
    // Empty / null for a project-less work item; the service routes on it.
    projectId: string | null | undefined,
    issueId: string,
    skillId: string,
    source: AnalyticsSource = "page",
  ): Promise<void> {
    const cache = this.workItemCache(workspaceSlug);
    const existing = this.rowBefore(cache, issueId);
    if (!existing) return;
    await this.guardWrite(
      cache,
      [issueId],
      () =>
        this.rootStore.workspaceSkillService.removeIssueSkill(
          workspaceSlug,
          projectId,
          issueId,
          skillId,
        ),
      {
        optimistic: () =>
          runInAction(() =>
            this.patchRow(cache, issueId, {
              skill_ids: existing.skill_ids.filter((id) => id !== skillId),
            }),
          ),
      },
    );
    track("task_skill_removed", {
      task_id: issueId,
      project_id: projectId || null,
      skill_id: skillId,
      source,
    });
  }

  async deleteIssue(
    workspaceSlug: string,
    projectId: string,
    issueId: string,
    source: AnalyticsSource = "page",
  ): Promise<void> {
    const cache = this.workItemCache(workspaceSlug);
    const before = this.rowBefore(cache, issueId);
    await this.rootStore.workItemService.deleteWorkItem(workspaceSlug, projectId, issueId);
    track("task_deleted", singleTaskProps(before, issueId, projectId, source));
    this.dropRows(cache, [issueId]);
  }

  async bulkDeleteIssues(
    workspaceSlug: string,
    projectId: string,
    issueIds: string[],
    source: AnalyticsSource = "page",
  ): Promise<void> {
    await this.rootStore.workItemService.bulkDeleteWorkItems(workspaceSlug, projectId, issueIds);
    track("task_deleted", { project_id: projectId, count: issueIds.length, source });
    this.dropRows(this.workItemCache(workspaceSlug), issueIds);
  }

  async updateIssueDates(
    workspaceSlug: string,
    projectId: string,
    updates: Array<{ id: string; start_date?: string | null; target_date?: string | null }>,
    source: AnalyticsSource = "page",
  ): Promise<void> {
    const cache = this.workItemCache(workspaceSlug);
    const relationsScope = queryCacheScope(workspaceSlug, "work-item-relations");
    const backup = new Map(
      updates
        .map((update) => {
          const issue = this.rowBefore(cache, update.id);
          return issue ? [update.id, { ...issue }] : undefined;
        })
        .filter(Boolean) as Array<[string, TIssue]>,
    );

    const response = await this.guardWrite(
      cache,
      updates.map((update) => update.id),
      () => this.rootStore.workItemService.updateWorkItemDates(workspaceSlug, projectId, updates),
      {
        optimistic: () =>
          runInAction(() => {
            updates.forEach((update) => {
              if (!backup.has(update.id)) return;
              this.patchRow(cache, update.id, {
                ...(update.start_date !== undefined ? { start_date: update.start_date } : {}),
                ...(update.target_date !== undefined ? { target_date: update.target_date } : {}),
              });
            });
          }),
      },
    );
    // The server snaps dates onto working days (M11, NG-36), so what it stored
    // can differ from what was dropped — a bar landing on a weekend or on a
    // covered assignee's holiday moves to the next day they work. Reconcile from
    // the response rather than leaving the optimistic guess on screen, or the
    // Gantt shows a bar the backend has already moved.
    runInAction(() => {
      (response?.resolved ?? []).forEach((resolved) => {
        // Sub-task rows read their parent's cached children (NG-1283), which
        // sit in the same domain, so this patch reaches them too.
        this.patchRow(cache, resolved.id, {
          start_date: resolved.start_date,
          target_date: resolved.target_date,
        });
      });
    });
    this.trackDateWrite(projectId, updates, backup, source);
    updates.forEach((update) => {
      this.markActivityStale(workspaceSlug, update.id);
    });
    // Dependencies panels hold their own peer dates, outside the work-item rows.
    // Every successful bulk move must refresh that family too (NG-3553).
    if (relationsScope) await rereadWorkItemRelations(cache.client, relationsScope);
  }

  async archiveIssue(
    workspaceSlug: string,
    projectId: string,
    issueId: string,
    source: AnalyticsSource = "page",
  ): Promise<{ archived_at: string }> {
    const cache = this.workItemCache(workspaceSlug);
    const before = this.rowBefore(cache, issueId);
    const response = await this.rootStore.issueArchiveService.archiveIssue(
      workspaceSlug,
      projectId,
      issueId,
    );
    runInAction(() => {
      this.moveIssueToArchived(cache, issueId, response.archived_at);
    });
    track("task_archived", singleTaskProps(before, issueId, projectId, source));
    this.relist(cache);
    return response;
  }

  async restoreIssue(
    workspaceSlug: string,
    projectId: string,
    issueId: string,
    source: AnalyticsSource = "page",
  ): Promise<void> {
    const cache = this.workItemCache(workspaceSlug);
    const before = this.rowBefore(cache, issueId);
    await this.rootStore.issueArchiveService.restoreIssue(workspaceSlug, projectId, issueId);
    runInAction(() => {
      this.moveIssueToActive(cache, issueId);
    });
    track("task_restored", singleTaskProps(before, issueId, projectId, source));
    this.relist(cache);
  }

  async bulkArchiveIssues(
    workspaceSlug: string,
    projectId: string,
    issueIds: string[],
    source: AnalyticsSource = "page",
  ): Promise<{ archived_at: string; succeeded: string[]; failed: string[] }> {
    const cache = this.workItemCache(workspaceSlug);
    const response = await this.rootStore.issueArchiveService.bulkArchiveIssues(
      workspaceSlug,
      projectId,
      issueIds,
    );
    runInAction(() => {
      issueIds.forEach((id) => this.moveIssueToArchived(cache, id, response.archived_at));
    });
    track("task_archived", { project_id: projectId, count: issueIds.length, source });
    this.relist(cache);
    return {
      archived_at: response.archived_at,
      succeeded: [...issueIds],
      failed: [],
    };
  }

  async bulkRestoreIssues(
    workspaceSlug: string,
    projectId: string,
    issueIds: string[],
    source: AnalyticsSource = "page",
  ): Promise<{ succeeded: string[]; failed: string[] }> {
    // The archive API only exposes single-issue restore. Dispatch one request
    // per id and patch only the restores that landed, so a partial outcome
    // shows as it is instead of rolling back the ones that succeeded.
    const results = await Promise.allSettled(
      issueIds.map((id) =>
        this.rootStore.issueArchiveService.restoreIssue(workspaceSlug, projectId, id),
      ),
    );

    const succeeded: string[] = [];
    const failed: string[] = [];
    results.forEach((result, index) => {
      if (result.status === "fulfilled") {
        succeeded.push(issueIds[index]);
      } else {
        failed.push(issueIds[index]);
      }
    });

    if (succeeded.length > 0) {
      const cache = this.workItemCache(workspaceSlug);
      runInAction(() => {
        succeeded.forEach((id) => this.moveIssueToActive(cache, id));
      });
      // One event for the action, counting only the restores that landed.
      track("task_restored", { project_id: projectId, count: succeeded.length, source });
      this.relist(cache);
    }

    return { succeeded, failed };
  }

  async bulkDeleteArchivedIssues(
    workspaceSlug: string,
    projectId: string,
    issueIds: string[],
    source: AnalyticsSource = "page",
  ): Promise<void> {
    // The archived list deletes one row through this bulk route too; that is a
    // single-task action, so it reports the task rather than a count.
    const single = source !== "bulk" && issueIds.length === 1;
    const cache = this.workItemCache(workspaceSlug);
    const before = single ? this.rowBefore(cache, issueIds[0]) : undefined;
    await this.rootStore.workItemService.bulkDeleteWorkItems(workspaceSlug, projectId, issueIds);
    track(
      "task_deleted",
      single
        ? singleTaskProps(before, issueIds[0], projectId, source)
        : { project_id: projectId, count: issueIds.length, source },
    );
    this.dropRows(cache, issueIds);
  }

  /**
   * One `task_updated` for a date write. The first update is the task the user
   * moved; any after it are dependents the gantt shifted with it, reported as
   * `count` so the drag still counts once.
   */
  private trackDateWrite(
    projectId: string,
    updates: Array<{ id: string; start_date?: string | null; target_date?: string | null }>,
    before: Map<string, TIssue>,
    source: AnalyticsSource = "page",
  ): void {
    const moved = updates[0];
    if (!moved) return;
    const fields = (["start_date", "target_date"] as const).filter((key) =>
      updates.some((update) => update[key] !== undefined),
    );
    track("task_updated", {
      task_id: moved.id,
      project_id: projectId,
      category: before.get(moved.id)?.category ?? null,
      fields,
      source,
      ...(updates.length > 1 ? { count: updates.length } : {}),
    });
  }

  /** Keep a parent's `sub_issues_count` in step after a child leaves its
   * cached sub-issue list (so the expand chevron disappears at zero). */
  private decrementSubIssuesCount(cache: WorkItemCache, parentId: string): void {
    const parent = this.rowBefore(cache, parentId);
    if (!parent) return;
    this.patchRow(cache, parentId, {
      sub_issues_count: Math.max(0, (parent.sub_issues_count ?? 1) - 1),
    });
  }

  /**
   * Which list an archived row belongs to is the lists' re-read; it leaves its
   * parent's children at once, as the server lists only active ones.
   */
  private moveIssueToArchived(cache: WorkItemCache, issueId: string, archivedAt: string): void {
    this.patchRow(cache, issueId, { archived_at: archivedAt });
    removeCachedSubTask(cache.client, cache.tenant, issueId).forEach((parentId) =>
      this.decrementSubIssuesCount(cache, parentId),
    );
  }

  /** The lists' re-read brings a restored child back under its parent too. */
  private moveIssueToActive(cache: WorkItemCache, issueId: string): void {
    this.patchRow(cache, issueId, { archived_at: null });
  }

  /**
   * Read a work item's FULL record by id or identifier at workspace scope.
   *
   * Never answered from a list row: a cached list row is the lean shape,
   * which has no `description_html`, and both callers that
   * render a description editor (the full page, the side panel) would seed it
   * empty and autosave over the saved text (NG-2422). A caller that only needs
   * fields a list row carries checks `getIssueById` first.
   *
   * The record is the cache's `workItemDetailQuery` entry, reused while fresh
   * unless `fresh` asks again (a re-read after a write), and the lists' rows of
   * it are brought up to date with it.
   */
  async fetchIssueByIdentifier(
    workspaceSlug: string,
    workItem: string,
    { fresh = false }: { fresh?: boolean } = {},
  ): Promise<TIssue> {
    const cache = this.workItemCache(workspaceSlug);
    const scope = scopeIfReady(workspaceSlug, "work item");
    if (scope) {
      const definition = workItemDetailQuery(this.rootStore.workItemService, scope, workItem);
      const issue = await cache.client.fetchQuery(
        fresh ? { ...definition, staleTime: 0 } : definition,
      );
      runInAction(() => patchCachedWorkItem(cache.client, cache.tenant, issue.id, issue));
      return issue;
    }
    const issue = await this.rootStore.workItemService.getWorkItemByIdentifier(
      workspaceSlug,
      workItem,
    );
    // Before the session resolves nothing is cached: the read stands alone.
    return issue;
  }
}
